// src/components/ProfileView.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import Purchase from './Purchase';
import { useLocation, useNavigate } from 'react-router-dom';

const ProfileView = () => {
  const { currentUser } = useAuth();
  const [energy, setEnergy] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const success = queryParams.get('success') === 'true';
  const canceled = queryParams.get('canceled') === 'true';

  const navigateBack = () => {
    navigate(-1);
  };

  const fetchEnergy = async () => {
    setLoading(true);
    setError('');
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setEnergy(Math.round(userDoc.data().energy || 0));
      } else {
        setError('User document does not exist.');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEnergy();
    // Optionally, set up a listener for real-time updates
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const navigateToHome = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        p: 2,
        px: 10,
        minHeight: '100vh',
        backgroundColor: 'var(--primary-bg-color)',
        color: 'var(--text-color)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      {/* Top bar (Title + Buttons) */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          width: '100%',
          maxWidth: '1200px',
          borderRadius: '0px',
          backgroundColor: 'transparent',
          border: 'none',
          mt: 2,
          mb: 1
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold', mr: 2 }}>
          Profile
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={navigateBack}
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff33',
              textTransform: 'none',
              fontSize: '1rem',
              mr: 1,
              '&:hover': {
                backgroundColor: '#333333',
                borderColor: '#ffffff'
              }
            }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            onClick={navigateToHome}
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff33',
              textTransform: 'none',
              fontSize: '1rem',
              mr: 1,
              '&:hover': {
                backgroundColor: '#333333',
                borderColor: '#ffffff'
              }
            }}
          >
            Home
          </Button>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              color: '#ffffff',
              borderColor: '#ffffff33',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#333333',
                borderColor: '#ffffff'
              }
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      <Divider
        sx={{
          my: 2,
          borderColor: '#ffffff33',
          width: '100%',
          maxWidth: '1200px'
        }}
      />

      {/* Centered Content */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
          Email: {currentUser.email}
        </Typography>

        {/* Success / Canceled / Error Alerts */}
        {success && (
          <Alert
            severity="success"
            sx={{
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
              mb: 2,
              borderRadius: '4px',
              border: '1px solid #333',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Success ✔️
          </Alert>
        )}
        {canceled && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
              mb: 2,
              borderRadius: '4px',
              border: '1px solid #333',
              width: '100%',
              textAlign: 'center'
            }}
          >
            Canceled ❌
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
              mb: 2,
              borderRadius: '4px',
              border: '1px solid #333',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Loading or Energy */}
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress sx={{ color: '#ffffff' }} />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Total Energy: {energy}
            </Typography>
          </Box>
        )}

        {/* Purchase Component */}
        <Purchase userId={currentUser.uid} onPurchaseSuccess={fetchEnergy} />
      </Box>
    </Box>
  );
};

export default ProfileView;
